import { ColorInput } from '@/components/ColorInput';
import { GenericSelect } from '@/components/GenericSelect';
import { SegmentationModal, SelectedLocation } from '@/components/SegmentationModal';
import { SelectMediaCard } from '@/components/SelectMediaCard';
import { SortableMediaCard } from '@/components/SortableMediaCard';
import { VideoModal } from '@/components/VideoModal';
import BaseLayout from '@/layout/BaseLayout';
import { useCampaignFormStore } from '@/screens/CampaignCreate/store';
import { trpc } from '@/trpcClient';
import { campaignDisplayFormatMapper } from '@/utils/mapper';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import { EDisplayFormat } from '@shelflix/schemas/_common/enum';
import { allowedColors } from '@shelflix/schemas/_common/validation';
import { checkCampaignCreateInput, type CreateCampaignInputType } from '@shelflix/schemas/campaign/create';
import type { BrandDto } from '@shelflix/trpc/routes/brand/list/useCase';
import type { MediaDto } from '@shelflix/trpc/routes/media/list/useCase';
import { DatePicker } from '@shelflix/ui-web/components/DatePicker/index';
import Dropzone, { CropArea } from '@shelflix/ui-web/components/Dropzone/index';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Label } from '@shelflix/ui-web/components/ui/label';
import { ScrollArea, ScrollBar } from '@shelflix/ui-web/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { Textarea } from '@shelflix/ui-web/components/ui/textarea';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addDays, subDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function CampaignCreate() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | string | null>(null);
  const [crop, setCrop] = useState<CropArea | undefined>(undefined);
  const { isMobile } = useDeviceDetect();

  const [thumbnailRemoved, setThumbnailRemoved] = useState(false);
  const { formData, resetForm } = useCampaignFormStore();
  const [brand, setBrand] = useState<BrandDto | null>(null);
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());

  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation[]>([]);

  const [availableMedias, setAvailableMedias] = useState<MediaDto[]>([]);
  const [selectedMedias, setSelectedMedias] = useState<MediaDto[]>([]);
  const [mediatoCampaign, setMediatoCampaign] = useState<MediaDto[]>([]);

  const navigate = useNavigate();

  const form = useForm<CreateCampaignInputType>({
    defaultValues: formData,
    mode: 'all',
  });

  // const [search, setSearch] = useState('');

  const query = useQuery({
    ...trpc.brand.list.queryOptions({
      // search: {
      //   fields: ['name', 'code'],
      //   value: search ?? '',
      // },
      recordsPerPage: 9999,
    }),
    // enabled: !!search,
  });

  const createCompaignMutation = useMutation(trpc.campaign.create.mutationOptions());

  const onSubmit = async (data: CreateCampaignInputType) => {
    if (createCompaignMutation.isPending || uploadImageMutation.isPending) return;
    if (!thumbnail) {
      toast.error('É obrigatório selecionar uma thumbnail para criar a campanha.');
      return;
    }
    if (thumbnailRemoved && typeof thumbnail === 'string') {
      toast.error('Selecione uma nova thumbnail antes de salvar.');
      return;
    }
    setIsSubmitting(true);

    try {
      // First, upload the thumbnail with compression if there's one
      let thumbnailKey: string | undefined;
      if (thumbnail instanceof File) {
        const base64Data = await fileToBase64(thumbnail);
        const uploadResult = await uploadImageMutation.mutateAsync({
          base64Data,
          filename: thumbnail.name,
          imageType: 'campaign-thumbnail',
          cropOptions: crop,
        });
        thumbnailKey = uploadResult.key;
        logger.info('Thumbnail uploaded', {
          originalSize: uploadResult.originalSize,
          compressedSize: uploadResult.compressedSize,
          savingsPercent: uploadResult.savingsPercent,
        });
      }

      const payload = {
        ...data,
        thumbnailKey, // Use pre-uploaded key if available
        segmentation: selectedLocation.map((location) => {
          return {
            uuid: location.uuid,
            name: location.name,
            isState: location.isState,
          };
        }),
        medias: mediatoCampaign.map((media) => media.uuid),
      } as CreateCampaignInputType;

      await createCompaignMutation.mutateAsync(payload);
      resetForm();
      navigate('/campaign', { state: { toastSuccess: true } });
    } catch (error) {
      toast.error(`Erro ao cadastrar campanha: ${(error as Error).message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const { data, isLoading, isError } = useQuery(
    trpc.media.list.queryOptions(
      {
        brandUuid: form.getValues('brand'),
        recordsPerPage: 999,
      },
      { enabled: !!form.getValues('brand') }
    )
  );

  const medias = data?.medias ?? [];

  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');
  const displayFormat = form.watch('displayFormat');

  // Filter available medias by the selected displayFormat (orientation)
  useEffect(() => {
    if (!isLoading && !isError) {
      const filteredMedias = displayFormat ? medias.filter((media) => media.displayFormat === displayFormat) : medias;
      setAvailableMedias(filteredMedias);
      // Clear selected medias that don't match the new displayFormat
      setSelectedMedias((prev) => prev.filter((m) => !displayFormat || m.displayFormat === displayFormat));
    }
  }, [data?.medias, isLoading, isError, displayFormat]);

  const onToggleMedia = (media: MediaDto, checked: boolean) => {
    setSelectedMedias((prev) => {
      if (checked) {
        if (!prev.some((m) => m.uuid === media.uuid)) {
          return [...prev, media];
        }
        return prev;
      } else {
        return prev.filter((m) => m.uuid !== media.uuid);
      }
    });
  };

  const handleAddMediaToCampaign = () => {
    setMediatoCampaign((prev) => {
      const newMedias = selectedMedias.filter((media) => !prev.some((m) => m.uuid === media.uuid));
      return [...prev, ...newMedias];
    });

    setSelectedMedias([]);
  };

  const handleRemoveMediaFromCampaign = (media: MediaDto) => {
    setMediatoCampaign((prev) => prev.filter((m) => m.uuid !== media.uuid));
  };

  const handleMoveMedia = (index: number, direction: 'left' | 'right') => {
    setMediatoCampaign((prev) => {
      const newOrder = [...prev];
      const targetIndex = direction === 'left' ? index - 1 : index + 1;

      if (targetIndex < 0 || targetIndex >= newOrder.length) return prev;

      [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];

      return newOrder;
    });
  };

  const removeLocation = (location: SelectedLocation) => {
    setSelectedLocation((prev) => prev.filter((loc) => loc.uuid !== location.uuid));
  };

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex w-full flex-col gap-8">
          <div className="flex flex-col flex-wrap gap-4 sm:flex-row">
            <Card className="flex flex-1 rounded-[10px]">
              <CardHeader className="pb-0">
                <CardTitle className="text-primary leading-5 font-semibold sm:text-2xl">
                  Informações de campanha
                </CardTitle>{' '}
              </CardHeader>
              <CardContent className="flex flex-col gap-[27px_12px]">
                <div className="grid grid-cols-7 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    rules={{ validate: (value) => checkCampaignCreateInput('name', value) }}
                    render={({ field }) => (
                      <FormItem className="col-span-7 flex flex-col gap-0.75 sm:col-span-3">
                        <FormLabel className="text-xs font-medium text-(--gray-primary)">Nome da Campanha</FormLabel>
                        <FormControl>
                          <Input className=" " placeholder="Digite o nome da campanha" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="brand"
                    rules={{ validate: (value) => checkCampaignCreateInput('brand', value) }}
                    render={({ field }) => (
                      <FormItem className="col-span-7 flex flex-col gap-0.75 sm:col-span-2">
                        <FormLabel className="text-xs font-medium text-(--gray-primary)">Marca</FormLabel>
                        <FormControl>
                          <GenericSelect<BrandDto>
                            onSelect={(brand) => {
                              field.onChange(brand.uuid ?? brand);
                              setBrand(brand);
                            }}
                            value={field.value}
                            loading={query.isLoading}
                            dependencies={[query.data]}
                            fetchItems={async (search) => {
                              const brands = (query.data?.brands ?? []).sort((a, b) => a.name.localeCompare(b.name));
                              if (!search) return brands;
                              return brands.filter((brand) => brand.name.toLowerCase().includes(search.toLowerCase()));
                            }}
                            renderItem={(brand) => (
                              <div>
                                <div className="text-sm font-medium">{brand.name}</div>
                                <div className="text-muted-foreground text-xs">{brand.category}</div>
                              </div>
                            )}
                            getItemLabel={(brand) => brand.name}
                            getItemKey={(brand) => brand.uuid}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="col-span-7 text-xs font-medium text-(--gray-primary) sm:col-span-2">
                    <Label className="pb-1 text-xs font-medium text-(--gray-primary)">Código de cor - LED</Label>
                    <ColorInput value={brand?.ledColor ?? '#fff'} disabled mode="select" options={allowedColors} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="displayFormat"
                    rules={{ validate: (value) => checkCampaignCreateInput('displayFormat', value) }}
                    render={({ field }) => (
                      <FormItem className="col-span-3 flex flex-col gap-0.75 sm:col-span-1">
                        <FormLabel className="text-xs font-medium text-(--gray-primary)">Formato de exibição</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value ?? ''}
                            onValueChange={(displayFormat: EDisplayFormat) => {
                              field.onChange(displayFormat);
                            }}
                          >
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                            <SelectContent className=" ">
                              {Object.values(EDisplayFormat).map((displayFormat) => (
                                <SelectItem key={displayFormat} value={displayFormat}>
                                  {campaignDisplayFormatMapper(displayFormat)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="startDate"
                    rules={{ validate: (value) => checkCampaignCreateInput('startDate', value) }}
                    render={({ field }) => (
                      <FormItem className="col-span-3 flex flex-col gap-0.75 sm:col-span-1">
                        <FormLabel className="text-xs font-medium text-(--gray-primary)">Data de Início</FormLabel>
                        <FormControl>
                          <DatePicker
                            mode="single"
                            onChange={(value) =>
                              field.onChange(value instanceof Date ? value?.toISOString() : undefined)
                            }
                            value={field.value ? new Date(field.value) : undefined}
                            toDate={endDate ? subDays(new Date(endDate), 1) : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="endDate"
                    rules={{ validate: (value) => checkCampaignCreateInput('endDate', value) }}
                    render={({ field }) => (
                      <FormItem className="col-span-3 flex flex-col gap-0.75 sm:col-span-1">
                        <FormLabel className="text-xs font-medium text-(--gray-primary)">
                          Data de Encerramento (não obrigatório)
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            mode="single"
                            onChange={(value) =>
                              field.onChange(value instanceof Date ? value?.toISOString() : undefined)
                            }
                            value={field.value ? new Date(field.value) : undefined}
                            fromDate={startDate ? addDays(new Date(startDate), 1) : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-0.75">
                      <FormLabel className="text-xs font-medium text-(--gray-primary)">Descrição</FormLabel>
                      <FormControl>
                        <Textarea className="min-h-32" rows={5} onInput={field.onChange} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <Card className="rounded-[10px]">
              <CardHeader className="pb-0">
                <CardTitle className="text-primary text-xl leading-5 font-semibold md:text-2xl">Thumbnail</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ maxWidth: 180 }} className="mx-auto aspect-9/16">
                  {thumbnail && typeof thumbnail === 'string' ? (
                    <div className="flex flex-col gap-4">
                      <div style={{ maxWidth: 180 }} className="mx-auto flex aspect-9/16 items-center justify-center">
                        <img
                          src={thumbnail}
                          alt="Thumbnail atual"
                          className="h-full w-full rounded object-cover"
                          onError={() => {
                            setThumbnail('https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png');
                          }}
                        />
                      </div>
                      <div className="flex justify-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setThumbnail(null);
                            setThumbnailRemoved(true);
                          }}
                          className="w-fit"
                        >
                          <Icon name="compare_arrows" />
                          Alterar thumbnail
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ maxWidth: 180 }} className="mx-auto aspect-9/16">
                      <Dropzone
                        removable={true}
                        onFilesSelected={(files) => {
                          setThumbnail(files[0]);
                          setThumbnailRemoved(false);
                        }}
                        title="Arraste ou clique aqui para selecionar a thumbnail"
                        description="Extensões permitidas: JPG, PNG. Proporção obrigatória: 9:16 (Máx. 5mb)"
                        value={typeof thumbnail === 'object' && thumbnail !== null ? [thumbnail as File] : undefined}
                        requiredAspectRatio="9:16"
                        className="h-full w-full"
                        readonly={false}
                        cropModal={true}
                        cropValue={crop}
                        onCropChange={(crop) => setCrop(crop)}
                        variant="thumb"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-[10px]">
            <CardHeader className="flex flex-col justify-between gap-4 pb-0 sm:flex-row">
              <CardTitle className="text-primary shrink-0 text-[18px] leading-5 font-semibold sm:text-2xl">
                Segmentações de conteúdo
              </CardTitle>
              <SegmentationModal
                trigger={
                  <div className="flex w-full grow">
                    <Button type="button" className="ml-auto grow sm:grow-0" onClick={handleAddMediaToCampaign}>
                      <Icon name="device_hub" />
                      <span>Adicionar segmentação</span>
                    </Button>
                  </div>
                }
                onSaveSegmentation={(locations) => setSelectedLocation((prev) => [...(prev ?? []), ...locations])}
              />
            </CardHeader>
            <CardContent className="mx-4 flex max-h-80 rounded-[10px] p-2">
              <ScrollArea type="always" className="w-1 flex-1 flex-row flex-nowrap">
                {selectedLocation.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedLocation.map((location) => (
                      <div
                        key={location.uuid}
                        className="text-primary flex w-81 items-start justify-between rounded-md border-2"
                      >
                        <div className="flex flex-col gap-2 p-2">
                          <span className="font-semibold">{location.name}</span>
                          <span className="flex flex-row items-center gap-2 font-['Figtree',Helvetica] font-normal text-(--input-color)">
                            <Icon name="devices" className="text-(--input-color)" size={16} />
                            {location.materialCount} Materiais
                          </span>
                        </div>
                        <Button variant="ghost" type="button" className="px-2" onClick={() => removeLocation(location)}>
                          <Icon name="delete" className="text-primary" size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </CardContent>
          </Card>

          <Card className="rounded-[10px]">
            <CardHeader className="flex flex-col justify-between gap-4 pb-0 sm:flex-row">
              <CardTitle className="text-primary shrink-0 text-[18px] leading-5 font-semibold sm:text-2xl">
                Mídias disponíveis
              </CardTitle>
              <div className="flex w-full grow">
                <Button
                  type="button"
                  className="ml-auto grow sm:grow-0"
                  disabled={selectedMedias.length === 0}
                  onClick={handleAddMediaToCampaign}
                >
                  <Icon name="add" />
                  <span>Adicionar na campanha</span>
                </Button>
              </div>
            </CardHeader>
            <section
              className="flex w-full flex-wrap overflow-hidden rounded-[5px]"
              style={{ gap: isMobile ? '8px' : '28px' }}
            >
              <Card className="grid min-h-32 w-full grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-2 p-2 py-2 md:grid-cols-[repeat(auto-fill,minmax(134px,1fr))] md:gap-4 lg:grid-cols-[repeat(auto-fill,minmax(144px,1fr))]">
                {!isLoading &&
                  !isError &&
                  !!availableMedias.length &&
                  availableMedias.map((media) => (
                    <SelectMediaCard
                      key={media.uuid}
                      media={media}
                      brandName={brand?.name ?? ''}
                      isSelected={selectedMedias.some((m) => m.uuid === media.uuid)}
                      onToggle={onToggleMedia}
                    />
                  ))}
              </Card>
            </section>
          </Card>

          <Card className="rounded-[10px]">
            <CardHeader className="flex flex-col justify-between gap-4 pb-0 sm:flex-row">
              <CardTitle className="text-primary shrink-0 text-[18px] leading-5 font-semibold sm:text-2xl">
                Edição de campanha
              </CardTitle>
              <div className="flex w-full grow">
                <VideoModal
                  videos={mediatoCampaign.map((media) => media.url)}
                  trigger={
                    <div className="flex w-full grow">
                      <Button type="button" className="ml-auto grow sm:grow-0">
                        <Icon name="play_circle" />
                        <span>Assistir prévia</span>
                      </Button>
                    </div>
                  }
                />
              </div>
            </CardHeader>
            <section
              className="flex w-full flex-wrap overflow-hidden rounded-[5px]"
              style={{ gap: isMobile ? '8px' : '28px' }}
            >
              <Card className="grid min-h-32 w-full grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-2 p-2 py-2 md:grid-cols-[repeat(auto-fill,minmax(134px,1fr))] md:gap-4 lg:grid-cols-[repeat(auto-fill,minmax(144px,1fr))]">
                {!isLoading &&
                  !isError &&
                  !!medias.length &&
                  mediatoCampaign.map((media, index) => (
                    <SortableMediaCard
                      key={media.uuid}
                      media={media}
                      onRemove={() => handleRemoveMediaFromCampaign(media)}
                      onMoveLeft={() => handleMoveMedia(index, 'left')}
                      onMoveRight={() => handleMoveMedia(index, 'right')}
                      disabledLeft={index === 0}
                      disabledRight={index === mediatoCampaign.length - 1}
                    />
                  ))}
              </Card>
            </section>
          </Card>

          <div className="mb-10 flex grow-0 justify-center">
            <Button
              loading={createCompaignMutation.isPending || isSubmitting}
              type="submit"
              className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-fit sm:grow-0"
            >
              <Icon name="save" color="#fff" />
              <span className="font-['Figtree'] text-sm">Salvar</span>
            </Button>
          </div>
        </form>
      </Form>
    </BaseLayout>
  );
}
